import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../util/types';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { AspectRatioWrapper, Button, IconClose, IconArrowHead, ResponsiveImage } from '../';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';
import FullscreenButton from '../FullscreenButton/FullscreenButton';

import css from './ListingImageGallery.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: false,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
    ? { aspectWidth: 4, aspectHeight: 3 }
    : hasDimensions
    ? { aspectWidth: w, aspectHeight: h }
    : { aspectWidth: 1, aspectHeight: 1 };
};

const ListingImageGallery = props => {
  const {
    intl,
    rootClassName,
    className,
    images,
    imageVariants,
    thumbnailVariants,
    aspectWidth,
    aspectHeight,
    thumbnailClassName,
    galleryRef,
    hideGalery,
    aditionalGalleryOptions,
    isDisplay,
    customOnScreenChange,
  } = props;

  const [isFullscreen, setIsFullscreen] = useState(galleryRef ? true : false);

  const thumbVariants = thumbnailVariants || imageVariants;
  // imageVariants are scaled variants.
  // const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(images?.[0], imageVariants[0]);
  const items = images.map((img, i) => {
    return {
      // We will only use the image resource, but react-image-gallery
      // requires the `original` key from each item.
      original: '',
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbAlt: intl.formatMessage(
        { id: 'ListingImageGallery.imageThumbnailAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbnail: img.attributes?.variants?.[thumbVariants[0]],
      imageDesktop: img.attributes?.variants?.[thumbVariants.at(-1)],
      image: img,
    };
  });

  const imageSizesMaybe = isFullscreen
    ? {}
    : { sizes: `(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px` };
  const renderItem = item => {
    return (
      <AspectRatioWrapper
        width={aspectWidth || 1}
        height={aspectHeight || 1}
        className={
          isFullscreen
            ? classNames(css.itemWrapperFullscreen, {
                [css.itemWrapperFullscreenDisplay]: isDisplay,
              })
            : css.itemWrapper
        }
        customAspectBox={css.customBox}
      >
        <div
          className={classNames(css.itemCentering, { [css.fullscreenItemCentering]: isFullscreen })}
        >
          {isFullscreen ? (
            <ResponsiveImage
              showAsBackground
              contain
              isFullscreen
              // rootClassName={css.item}
              rootClassName={isFullscreen ? css.item : css.itemWithoutMaxHeight}
              image={item.image}
              alt={item.alt}
              variants={imageVariants}
              isDisplay={isDisplay}
              {...imageSizesMaybe}
            />
          ) : (
            <div
              key={item?.image?.id?.uuid}
              className={css.coverImage}
              style={{ backgroundImage: `url(${item?.imageDesktop?.url})` }}
            ></div>
          )}
        </div>
      </AspectRatioWrapper>
    );
  };
  const renderThumbInner = item => {
    return (
      <div
        key={item?.image?.id?.uuid}
        className={classNames(css.galleryThumbnail, thumbnailClassName)}
        style={{ backgroundImage: `url(${item?.thumbnail?.url})` }}
      ></div>
    );
  };

  const onScreenChange = isFull => {
    setIsFullscreen(isFull);

    if (typeof hideGalery === 'function') {
      hideGalery(isFull);
    }
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button
        className={css.navLeft}
        disabled={disabled}
        onClick={e => {
          e.preventDefault();
          onClick();
        }}
      >
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>
    );
  };

  const renderRightNav = (onClick, disabled) => {
    return (
      <button
        className={css.navRight}
        disabled={disabled}
        onClick={e => {
          e.preventDefault();
          onClick();
        }}
      >
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>
    );
  };

  const renderFullscreenButton = (onClick, isFullscreen) => {
    if (isFullscreen) {
      return (
        <Button
          onClick={onClick}
          rootClassName={css.close}
          title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
        >
          <span className={css.closeText}>
            <FormattedMessage id="ListingImageGallery.closeModal" />
          </span>
          <IconClose rootClassName={css.closeIcon} />
        </Button>
      );
    }

    return null;
  };

  const renderOpenFullscreenButton = () => <FullscreenButton onClick={customOnScreenChange} />;

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <ReactImageGallery
      ref={galleryRef}
      additionalClass={classes}
      items={items}
      renderItem={renderItem}
      renderThumbInner={renderThumbInner}
      onScreenChange={onScreenChange}
      renderLeftNav={renderLeftNav}
      renderRightNav={renderRightNav}
      renderFullscreenButton={
        isFullscreen
          ? renderFullscreenButton
          : customOnScreenChange
          ? renderOpenFullscreenButton
          : undefined
      }
      {...(!isFullscreen ? aditionalGalleryOptions : {})}
      {...IMAGE_GALLERY_OPTIONS}
    />
  );
};

ListingImageGallery.defaultProps = {
  rootClassName: null,
  className: null,
  thumbnailVariants: null,
};

const { string, arrayOf } = PropTypes;

ListingImageGallery.propTypes = {
  rootClassName: string,
  className: string,
  images: arrayOf(propTypes.image).isRequired,
  imageVariants: arrayOf(string).isRequired,
  thumbnailVariants: arrayOf(string),

  // from injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(ListingImageGallery);
